import { useState, useEffect, memo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Alert,
} from '@mui/material'
import { Close, GetApp } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CertificateIcon } from 'src/v3/core/icons'
import { downloadFile } from 'src/v3/utilities/filesystem'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch } from 'src/store/hooks'
import {
  successMessageChanged,
  errorMessageChanged,
} from 'src/v3/manage/manage.slice'
import { extractCertificateType } from 'src/v3/manage/utils/certificate-download'
import CopyToClipboardV4 from 'src/v3/manage/components/copy-to-clipboard'
import type { RosterCEActivityData } from '../../types'
import NotificationSnackbars from './notification-snackbars'

dayjs.extend(relativeTime)

// Utility to properly format camelCase/kebab-case strings to human readable labels
function formatLabel(str: string): string {
  return (
    str
      // Handle kebab-case (ce-completion -> CE Completion)
      .replace(/-/g, ' ')
      // Handle camelCase but preserve consecutive capitals (certificateID -> Certificate ID, ceHours -> CE Hours)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Split consecutive capitals (ID, CE) from other letters
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // Capitalize each word
      .replace(/\b\w/g, (l) => l.toUpperCase())
      // Fix specific cases where we want all caps
      .replace(/\bCe\b/g, 'CE')
      .replace(/\bId\b/g, 'ID')
  )
}

interface CEActivityModalProps {
  event: RosterCEActivityData | null
  eventType: string | null
  isOpen: boolean
  onClose: () => void
  linkedServiceID: string
}

let CEActivityModal = memo(
  ({ event, eventType, isOpen, onClose }: CEActivityModalProps) => {
    let dispatch = useAppDispatch()

    // Download state
    let [downloading, setDownloading] = useState(false)
    let [errorMessage, setErrorMessage] = useState('')

    // Reset error message and global messages when modal is closed
    useEffect(() => {
      if (!isOpen) {
        setErrorMessage('')
        dispatch(successMessageChanged(''))
        dispatch(errorMessageChanged(''))
      }
    }, [isOpen, dispatch])

    // Get certificate download function (from certificate-list.tsx pattern)
    let getCertificate = (id: string, type: string) =>
      axios
        .get(`/api/v2/manage/certificate/export/${id}/${type}`)
        .then(pluckData)

    // Handle certificate download
    let handleDownload = async () => {
      if (!event?.certificateID) return

      setDownloading(true)
      setErrorMessage('')

      try {
        let certificateType = extractCertificateType(eventType || undefined)
        let response = await getCertificate(
          event.certificateID,
          certificateType,
        )
        let fileResponse = await axios.get(response, { responseType: 'blob' })
        let filename = event.certificateFileName || `${event.certificateID}.pdf`

        await downloadFile(fileResponse.data, filename)
      } catch (err: any) {
        setErrorMessage(
          `PDF Download Failed: ${err.response?.data || err.message || 'Unknown error'}`,
        )
      } finally {
        setDownloading(false)
      }
    }

    if (!event) {
      return null
    }

    // Format event data for display
    let displayData = [
      {
        key: 'Certificate ID',
        value: event.certificateID,
        type: 'text',
        isCopyable: true,
      },
      {
        key: 'Activity Type',
        value: event.activityType,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'Category',
        value: event.category,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'Course Name',
        value: event.courseTitle,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'Course Code',
        value: event.courseCode,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'CE Hours',
        value: event.ceHours,
        type: 'number',
        isCopyable: false,
      },
      {
        key: 'Completion Date',
        value: event.completionDate,
        type: 'datetime',
        isCopyable: false,
      },
      {
        key: 'License Types',
        value: event.licenseTypes.join(', '),
        type: 'text',
        isCopyable: false,
      },
    ].filter((item) => item.value)

    let formatDisplayValue = (value: any, type: string): string => {
      if (!value) return 'N/A'

      switch (type) {
        case 'datetime':
          return dayjs(value).format('MMM D, YYYY [at] h:mm A')
        case 'date':
          return dayjs(value).format('MMM D, YYYY')
        case 'number':
          return String(value)
        default:
          return String(value)
      }
    }

    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        aria-labelledby="ce-activity-modal-title"
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              height: '80vh',
              maxWidth: 'none',
              maxHeight: 'none',
              borderRadius: 2,
              bgcolor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" id="ce-activity-modal-title">
                CE Activity
              </Typography>
              <Chip
                label={formatLabel(event.activityType || 'CE Completion')}
                sx={{
                  bgcolor: 'entityColors.ceActivity',
                  color: 'entityColors.ceActivity',
                  fontWeight: 500,
                  '&.MuiChip-root': {
                    backgroundColor: (theme) =>
                      `${theme.palette.entityColors.ceActivity}20`,
                    color: (theme) => theme.palette.entityColors.ceActivity,
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close dialog"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* Event Metadata */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {dayjs(event.completionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
              {dayjs(event.completionDate).fromNow()}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {event.courseTitle} {event.courseCode && `(${event.courseCode})`}
            </Typography>
          </Box>

          {/* Activity Details Section */}
          <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CertificateIcon />
              Activity Details
            </Typography>

            {/* All Activity Details */}
            <Box className="grid grid-cols-1 gap-1 sm:grid-cols-2 mb-3">
              {displayData.map(({ key, value, type, isCopyable }) => {
                // Special rendering for Category - show as chip
                if (key === 'Category') {
                  return (
                    <Box key={key} sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        {formatLabel(key)}
                      </Typography>
                      <Chip
                        label={value}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )
                }

                return (
                  <Box key={key} sx={{ p: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      {formatLabel(key)}
                    </Typography>
                    {isCopyable ? (
                      <CopyToClipboardV4 value={String(value)} label={key} />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDisplayValue(value, type)}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>

            {/* Error Alert */}
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} role="alert">
                {errorMessage}
              </Alert>
            )}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} autoFocus>
            Close
          </Button>
          {event.hasCertificate && event.certificateID && (
            <Button
              startIcon={!downloading ? <GetApp /> : undefined}
              variant="outlined"
              onClick={handleDownload}
              loading={downloading}
              aria-label={
                downloading ? 'Downloading certificate' : 'Download certificate'
              }
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          )}
        </DialogActions>
        <NotificationSnackbars />
      </Dialog>
    )
  },
)

export default CEActivityModal
